/**
 * Ministry Service
 * Handles ministry management operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class MinistryService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MinistryService.DEFAULT_SERVICE_PATH,
            basePath: MinistryService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all ministries with optional active status filter
     * @param isActive - Optional filter for active/inactive ministries
     * @returns Array of ministries
     */
    async getAll(isActive) {
        const response = await this.getAllRaw(isActive);
        return response.data ?? [];
    }
    /**
     * Get all ministries with optional active status filter (raw response)
     * @param isActive - Optional filter for active/inactive ministries
     * @returns Raw response with array of ministries
     */
    getAllRaw(isActive) {
        const params = isActive !== undefined ? { isActive } : {};
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get ministry by ID
     * @param id - Ministry ID
     * @returns Ministry details
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Ministry with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get ministry by ID (raw response)
     * @param id - Ministry ID
     * @returns Raw response with ministry details
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== MINISTRY MEMBERS ====================
    /**
     * Get all members in a ministry
     * @param id - Ministry ID
     * @returns Array of ministry members
     */
    async getMinistryMembers(id) {
        const response = await this.getMinistryMembersRaw(id);
        return response.data ?? [];
    }
    /**
     * Get all members in a ministry (raw response)
     * @param id - Ministry ID
     * @returns Raw response with array of ministry members
     */
    getMinistryMembersRaw(id) {
        const url = this.buildUrl(`${id}/members`);
        return this.http.get(url);
    }
    /**
     * Get enriched member views for a ministry (with names, emails, avatars, roles)
     * @param id - Ministry ID
     * @returns Array of enriched ministry member views
     */
    async getMinistryMemberViews(id) {
        const response = await this.getMinistryMemberViewsRaw(id);
        return response.data ?? [];
    }
    /**
     * Get enriched member views for a ministry (raw response)
     * @param id - Ministry ID
     * @returns Raw response with array of enriched ministry member views
     */
    getMinistryMemberViewsRaw(id) {
        const url = this.buildUrl(`${id}/members/view`);
        return this.http.get(url);
    }
    /**
     * Ministries a member belongs to (ministry and role names), active first.
     * Admin role, the Ministry right or Member.Department.
     * GET /api/ministries/member/{memberId}/view?includeInactive=
     * @param memberId - Member ID
     * @param includeInactive - Also return inactive memberships (default false)
     */
    async getMinistryViewsByMember(memberId, includeInactive) {
        const response = await this.getMinistryViewsByMemberRaw(memberId, includeInactive);
        return response.data ?? [];
    }
    getMinistryViewsByMemberRaw(memberId, includeInactive) {
        const url = this.buildUrlWithParams(`member/${memberId}/view`, { includeInactive: includeInactive ? true : undefined });
        return this.http.get(url);
    }
    /**
     * Add a member to a ministry
     * @param ministryId - Ministry ID
     * @param memberId - Member ID
     * @param data - Member assignment data
     * @returns Created or reactivated member assignment
     */
    async addMemberToMinistry(ministryId, memberId, data) {
        const response = await this.addMemberToMinistryRaw(ministryId, memberId, data);
        if (!response.data) {
            throw new Error('Failed to add member to ministry');
        }
        return response.data;
    }
    /**
     * Add a member to a ministry (raw response)
     * @param ministryId - Ministry ID
     * @param memberId - Member ID
     * @param data - Member assignment data
     * @returns Raw response with created or reactivated member assignment
     */
    addMemberToMinistryRaw(ministryId, memberId, data) {
        const url = this.buildUrl(`${ministryId}/members/${memberId}`);
        return this.http.post(url, data);
    }
    /**
     * Change an active member's role in a ministry (PUT /api/ministries/{ministryId}/members/{memberId}).
     * roleID null clears the role. 404 when the member is not an active member.
     */
    async updateMinistryMember(ministryId, memberId, data) {
        const response = await this.updateMinistryMemberRaw(ministryId, memberId, data);
        if (!response.data) {
            throw new Error(response.message || `Failed to update member ${memberId} in ministry ${ministryId}`);
        }
        return response.data;
    }
    updateMinistryMemberRaw(ministryId, memberId, data) {
        const url = this.buildUrl(`${ministryId}/members/${memberId}`);
        return this.http.put(url, data);
    }
    /**
     * Remove a member from a ministry (soft delete)
     * @param ministryId - Ministry ID
     * @param memberId - Member ID
     * @returns Success status
     */
    async removeMemberFromMinistry(ministryId, memberId) {
        const response = await this.removeMemberFromMinistryRaw(ministryId, memberId);
        return response.success;
    }
    /**
     * Remove a member from a ministry (raw response)
     * @param ministryId - Ministry ID
     * @param memberId - Member ID
     * @returns Raw response with success status
     */
    removeMemberFromMinistryRaw(ministryId, memberId) {
        const url = this.buildUrl(`${ministryId}/members/${memberId}`);
        return this.http.delete(url);
    }
    // ==================== MINISTRY ACTIVITIES ====================
    /**
     * Get all activities for a ministry
     * @param id - Ministry ID
     * @returns Array of ministry activities
     */
    async getMinistryActivities(id) {
        const response = await this.getMinistryActivitiesRaw(id);
        return response.data ?? [];
    }
    /**
     * Get all activities for a ministry (raw response)
     * @param id - Ministry ID
     * @returns Raw response with array of ministry activities
     */
    getMinistryActivitiesRaw(id) {
        const url = this.buildUrl(`${id}/activities`);
        return this.http.get(url);
    }
    // ==================== MINISTRY EQUIPMENT ====================
    /**
     * Get all equipment for a ministry
     * @param id - Ministry ID
     * @returns Array of ministry equipment
     */
    async getMinistryEquipment(id) {
        const response = await this.getMinistryEquipmentRaw(id);
        return response.data ?? [];
    }
    /**
     * Get all equipment for a ministry (raw response)
     * @param id - Ministry ID
     * @returns Raw response with array of ministry equipment
     */
    getMinistryEquipmentRaw(id) {
        const url = this.buildUrl(`${id}/equipment`);
        return this.http.get(url);
    }
    // ==================== MINISTRY TEAMS ====================
    /**
     * Get all teams for a ministry
     * @param id - Ministry ID
     * @returns Array of ministry teams
     */
    async getMinistryTeams(id) {
        const response = await this.getMinistryTeamsRaw(id);
        return response.data ?? [];
    }
    /**
     * Get all teams for a ministry (raw response)
     * @param id - Ministry ID
     * @returns Raw response with array of ministry teams
     */
    getMinistryTeamsRaw(id) {
        const url = this.buildUrl(`${id}/teams`);
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new ministry
     * @param data - Ministry creation data
     * @returns Created ministry
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create ministry');
        }
        return response.data;
    }
    /**
     * Create a new ministry (raw response)
     * @param data - Ministry creation data
     * @returns Raw response with created ministry
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing ministry
     * @param id - Ministry ID
     * @param data - Ministry update data
     * @returns Updated ministry
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update ministry with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing ministry (raw response)
     * @param id - Ministry ID
     * @param data - Ministry update data
     * @returns Raw response with updated ministry
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Soft delete a ministry
     * @param id - Ministry ID
     * @returns Success status
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Soft delete a ministry (raw response)
     * @param id - Ministry ID
     * @returns Raw response with success status
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== MODEL VIEWS ====================
    /**
     * Get ministry ModelView by ID
     * @param id - Ministry ID
     * @returns Ministry ModelView with enriched data
     */
    async getMinistryView(id) {
        const response = await this.getMinistryViewRaw(id);
        if (!response.data) {
            throw new Error(`Ministry view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get ministry ModelView by ID (raw response)
     * @param id - Ministry ID
     * @returns Raw response with ministry ModelView
     */
    getMinistryViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Get all ministry ModelViews
     * @returns Array of ministry ModelViews
     */
    async getAllMinistryViews() {
        const response = await this.getAllMinistryViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all ministry ModelViews (raw response)
     * @returns Raw response with array of ministry ModelViews
     */
    getAllMinistryViewsRaw() {
        const url = this.buildUrl('view');
        return this.http.get(url);
    }
    /**
     * Get active ministry ModelViews only
     * @returns Array of active ministry ModelViews
     */
    async getActiveMinistryViews() {
        const response = await this.getActiveMinistryViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get active ministry ModelViews only (raw response)
     * @returns Raw response with array of active ministry ModelViews
     */
    getActiveMinistryViewsRaw() {
        const url = this.buildUrl('view/active');
        return this.http.get(url);
    }
    // ==================== CACHE ====================
    /**
     * Invalidate ministry cache
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate ministry cache (raw response)
     * @returns Raw response with success status
     */
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
MinistryService.DEFAULT_SERVICE_PATH = 'departments';
MinistryService.BASE_PATH = 'api/ministries';
//# sourceMappingURL=MinistryService.js.map